/**
 * RideTracker - Persistent GPS ride tracking for BareMC
 * Uses IndexedDB for point storage and localStorage for ride state.
 * Survives app switches, page reloads, and browser background states.
 */

const DB_NAME = 'baremc_ride_tracking';
const DB_VERSION = 1;
const STORE_NAME = 'points';
const STATE_KEY = 'baremc_ride_state';

class RideTracker {
  constructor() {
    this.watchId = null;
    this.db = null;
    this.onPointCallback = null;
    this.lastLat = null;
    this.lastLng = null;
    this._dbReady = this._initDB();
  }

  // Initialize IndexedDB
  _initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
      };
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async _ensureDB() {
    if (!this.db) await this._dbReady;
  }

  // Save a GPS point to IndexedDB
  async savePoint(point) {
    await this._ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.add(point);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // Load all stored points from IndexedDB
  async loadPoints() {
    await this._ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Clear all points from IndexedDB
  async clearPoints() {
    await this._ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // Get count of stored points
  async getPointCount() {
    await this._ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Save ride state to localStorage (survives page reload)
  saveState(state) {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {}
  }

  // Load ride state from localStorage
  loadState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  // Clear ride state
  clearState() {
    localStorage.removeItem(STATE_KEY);
  }

  // Haversine distance in meters
  _getDistanceM(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Start GPS watching with aggressive settings for ride tracking
  startTracking(onPoint, minDistanceM = 5) {
    if (this.watchId !== null) return;
    if (!navigator.geolocation) return;

    this.onPointCallback = onPoint;

    // ── Stale-GPS detektion ────────────────────────────────────────────────
    // iOS Safari serverer cached positions i baggrunden når browser-tabben
    // mister høj prioritet (fx Spotify banner / skærm dim). Vi holder øje
    // med hvor mange konsekutive callbacks der peger på næsten SAMME
    // koordinat — hvis >6, tvinger vi en genstart af watchPosition med
    // maximumAge:0 for at bede iOS om et frisk GPS-fix.
    this._sameCoordCount = 0;
    this._lastAcceptedTs = Date.now();

    const handlePosition = async (position, source) => {
      // Safety: hvis stopTracking blev kaldt mens et callback var in-flight
      // fra browserens queue, må vi ikke skrive stale data.
      if (this.onPointCallback === null && !this._maybeForceRefresh) return;

      const { latitude, longitude, speed, accuracy, heading } = position.coords;

      // Drop kun katastrofalt dårlige reads. 100m grænsen droppede normale
      // motorvejs-readings på iPhone (85-150m på fart). 500m er stadig
      // brugbar i replay + fanger den ægte skrald.
      if (accuracy > 500) {
        this._sameCoordCount++;
        if (this._maybeForceRefresh) this._maybeForceRefresh();
        return;
      }

      // Detekt "iOS serverer samme cached coord" — hvis vi får >6 callbacks
      // med bevægelse < 2m, er GPS-chippen sandsynligvis suspenderet.
      if (this.lastLat !== null) {
        const d = this._getDistanceM(this.lastLat, this.lastLng, latitude, longitude);
        if (d < 2) {
          this._sameCoordCount++;
          if (this._maybeForceRefresh) this._maybeForceRefresh();
          // Stadig skip afstands-filter når vi genkender cached-loop
          if (d < minDistanceM) return;
        } else {
          this._sameCoordCount = 0;
          if (d < minDistanceM) return; // normal for-tæt-drop
        }
      }

      // Impossible teleportation (> 300km/h). Vi tillader dog at referencen
      // opdateres hvis den samme "teleport"-position kommer 3 gange i træk —
      // ellers kan et enkelt GPS-hop låse trackeren fast for evigt.
      if (this.lastLat !== null && this._lastTimestamp) {
        const distM = this._getDistanceM(this.lastLat, this.lastLng, latitude, longitude);
        const timeSec = (Date.now() - this._lastTimestamp) / 1000;
        if (timeSec > 0 && timeSec < 60) {
          const speedMs = distM / timeSec;
          if (speedMs > 83) {
            this._teleportRejects = (this._teleportRejects || 0) + 1;
            if (this._teleportRejects < 3) return;
            // Fald igennem: efter 3 rejects, accepter og re-sync
          } else {
            this._teleportRejects = 0;
          }
        }
      }

      this.lastLat = latitude;
      this.lastLng = longitude;
      this._lastTimestamp = Date.now();
      this._lastAcceptedTs = Date.now();
      this._sameCoordCount = 0;

      const point = {
        latitude,
        longitude,
        speed: speed || 0,
        accuracy: accuracy || 0,
        heading: heading || 0,
        timestamp: Date.now(),
      };

      await this.savePoint(point);
      if (this.onPointCallback) this.onPointCallback(point);
    };

    // Watchdog + force-refresh: hvis GPS er suspenderet af iOS, genstart
    // watchPosition med maximumAge:0 for at kick-starte chippen.
    this._maybeForceRefresh = () => {
      if (this._sameCoordCount < 6) return;
      this._sameCoordCount = 0;
      try {
        if (this.watchId !== null) {
          navigator.geolocation.clearWatch(this.watchId);
          this.watchId = null;
        }
      } catch {}
      // Bed om en frisk position (ingen cache) og genstart watch.
      navigator.geolocation.getCurrentPosition(
        (pos) => handlePosition(pos, 'refresh'),
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 8000 }
      );
      this.watchId = navigator.geolocation.watchPosition(
        (pos) => handlePosition(pos, 'watch'),
        (err) => console.warn('RideTracker GPS error:', err.code, err.message),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
      );
    };

    // Main watch — maximumAge:0 tvinger iOS til IKKE at levere en cached
    // position. Uden dette kunne iOS servere samme koordinat i minutter
    // hvis browseren mistede fokus kortvarigt, og hele ruten frøs.
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => handlePosition(pos, 'watch'),
      (error) => {
        console.warn('RideTracker GPS error:', error.code, error.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      }
    );

    // Backup poll: hver 5s henter en frisk position — også dette skal have
    // maximumAge:0 for at komme forbi iOS-cache-loopet.
    this._backupInterval = setInterval(() => {
      // Watchdog: hvis intet punkt er accepteret i 60s under aktiv tur,
      // gennemtving et fuldt refresh af watch-strømmen.
      const stalledMs = Date.now() - (this._lastAcceptedTs || Date.now());
      if (stalledMs > 60000) {
        this._sameCoordCount = 6; // trigger force-refresh
        this._maybeForceRefresh();
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => handlePosition(pos, 'backup'),
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
      );
    }, 5000);
  }

  // Stop GPS watching
  stopTracking() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this._backupInterval) {
      clearInterval(this._backupInterval);
      this._backupInterval = null;
    }
    this.lastLat = null;
    this.lastLng = null;
    this._lastTimestamp = null;
    this._sameCoordCount = 0;
    this._teleportRejects = 0;
    this._lastAcceptedTs = null;
    this._maybeForceRefresh = null;
    this.onPointCallback = null;
  }

  // Check if tracking is active
  isTracking() {
    return this.watchId !== null;
  }
}

// Singleton instance
const rideTracker = new RideTracker();
export default rideTracker;
